import { getStoredActiveTenantId, clearStoredActiveTenantId } from './tenantStorage';

const KEY = 'tark.accountBoundary';
let userId: string | null = null;
let revision = readRevision();
const listeners = new Set<() => void>();
const remoteListeners = new Set<() => void>();

function readRevision(): string {
  try { return typeof window === 'undefined' ? '' : window.localStorage.getItem(KEY) || ''; }
  catch { return ''; }
}
function changed(): Error { return new Error('Account or workspace changed. Reopen this page and try again.'); }
function notify() { listeners.forEach(fn => fn()); }
function publish(id: string | null) {
  userId = id;
  revision = JSON.stringify({ userId: id, nonce: crypto.randomUUID() });
  if (typeof window !== 'undefined') window.localStorage.setItem(KEY, revision);
  notify();
}

export function getAccountRevision(): string { return readRevision(); }
export function getObservedAccountId(): string | null { return userId; }
export function getAccountBoundaryKey(): string { return `${revision}:${userId || 'signed-out'}`; }
export function subscribeAccountBoundary(fn: () => void) { listeners.add(fn); return () => { listeners.delete(fn); }; }
export function subscribeRemoteAccountChange(fn: () => void) { remoteListeners.add(fn); return () => { remoteListeners.delete(fn); }; }

export function invalidateChangedAccount(): boolean {
  const current = readRevision();
  if (current === revision) return false;
  revision = current; userId = null;
  clearStoredActiveTenantId();
  remoteListeners.forEach(fn => fn()); notify();
  return true;
}
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => { if (event.key === KEY || event.key === null) invalidateChangedAccount(); });
  window.addEventListener('focus', invalidateChangedAccount);
}

/** Call before a cookie-changing login/logout request, so other tabs stop immediately. */
export function beginAccountTransition() { clearStoredActiveTenantId(); publish(null); }
export function forgetObservedAccount() { userId = null; clearStoredActiveTenantId(); notify(); }
export function observeAuthenticatedAccount(id: number, requestRevision: string) {
  if (requestRevision !== readRevision()) throw changed();
  const next = String(id);
  if (!Number.isSafeInteger(id) || id <= 0) throw changed();
  let sharedId: unknown;
  try { sharedId = JSON.parse(revision).userId; } catch { sharedId = null; }
  if (sharedId === next) { userId = next; notify(); }
  else publish(next);
}

export type ActionOwner = { account: string; revision: string; tenant: number | null };
export function captureActionOwner(): ActionOwner {
  invalidateChangedAccount();
  if (!userId) throw changed();
  return { account: userId, revision, tenant: getStoredActiveTenantId() };
}
export function assertActionOwner(owner: ActionOwner, tenant = true) {
  invalidateChangedAccount();
  if (!userId || owner.account !== userId || owner.revision !== revision || readRevision() !== revision
      || tenant && owner.tenant !== getStoredActiveTenantId()) throw changed();
}

/** Capture at render/open time and carry the assertion through every async/retry boundary. */
export function ownedActionConfig() {
  let owner: ActionOwner;
  try { owner = captureActionOwner(); } catch { return { recoverAccountChange: true, assertRequestCurrent: () => { throw changed(); }, headers: {} }; }
  return { recoverAccountChange: true, assertRequestCurrent: () => assertActionOwner(owner), headers: {
    'X-TARK-Expected-User-Id': owner.account,
    ...(owner.tenant ? { 'X-TARK-Tenant-Id': String(owner.tenant) } : {}),
  } };
}
